/**
 * Device sessions — every signed-in device registers itself in
 * `sessions/{sessionId}` (via /api/sessions) so the owner can see where each
 * account is signed in and sign devices out from /sessions.
 *
 * Budget (shared free-tier Firestore): one API call on sign-in and at most one
 * per HEARTBEAT_MS while the app is visible (1 read + 1 write server-side),
 * plus one listener on this device's own doc so an owner sign-out lands
 * instantly. The listener never writes, so there is no snapshot-echo loop.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { currentVersion } from './appVersion';
import { currentDeviceInfo } from './deviceInfo';

const API_BASE = process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3001'
    : 'https://my-app-ramakrishna5s-projects.vercel.app');

const STORE_KEY = 'db_device_session';     // { id, uid, lastBeat, version }
const DEVICE_KEY = 'db_device_id';          // this browser, across sign-ins (never cleared)
const NOTICE_KEY = 'db_signout_notice';    // shown once on /login
const HEARTBEAT_MS = 30 * 60 * 1000;
const CHECK_EVERY_MS = 5 * 60 * 1000;

/** 128-bit random hex id (session ids and device ids). */
function newId() {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ── Local storage ────────────────────────────────────────────────────────────

function readStore() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch { return null; }
}
function writeStore(v) {
  try { if (v) localStorage.setItem(STORE_KEY, JSON.stringify(v)); else localStorage.removeItem(STORE_KEY); } catch {}
}

/** This device's session id for `uid` (created on first call). */
function ensureSession(uid) {
  const cur = readStore();
  if (cur && cur.uid === uid && cur.id) return cur;
  const next = { id: newId(), uid, lastBeat: 0 };
  writeStore(next);
  return next;
}

/** The id the owner page uses to mark "This device". */
export function currentSessionId() {
  return readStore()?.id || null;
}

/** Forget this device's session — called whenever auth reports signed-out. */
export function clearSession() { writeStore(null); }

export function setSignOutNotice(text) { try { sessionStorage.setItem(NOTICE_KEY, text); } catch {} }
export function peekSignOutNotice() {
  try { return sessionStorage.getItem(NOTICE_KEY) || ''; } catch { return ''; }
}
export function clearSignOutNotice() { try { sessionStorage.removeItem(NOTICE_KEY); } catch {} }

// ── API ──────────────────────────────────────────────────────────────────────

export async function sessionsApi(user, payload, { timeoutMs = 10000, forceRefresh = false } = {}) {
  const idToken = await user.getIdToken(forceRefresh);
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl && setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    let res;
    try {
      res = await fetch(`${API_BASE}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
        signal: ctrl?.signal,
      });
    } catch {
      // Offline, timed out, or the API is missing (a 404 preflight surfaces
      // in the browser as a CORS error).
      throw new Error('Couldn’t reach the server. Check your connection and try again.');
    }
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      const e = new Error(json.error || `Request failed (${res.status})`);
      e.revoked = !!json.revoked;
      throw e;
    }
    return json;
  } finally { if (timer) clearTimeout(timer); }
}

/**
 * Stable id for this browser. Unlike the session id it survives sign-out, so
 * a name the owner gives a device ("Kitchen iPad") sticks to the device.
 */
function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) { id = newId(); localStorage.setItem(DEVICE_KEY, id); }
    return id;
  } catch { return null; }
}

function deviceInfo() {
  // Build id, so the owner page can flag devices still on an old version.
  return { ...currentDeviceInfo(), version: currentVersion() };
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

let inflight = null; // shared across StrictMode double-mounts

/**
 * Register this device for `user` and keep it fresh; calls `onRevoked` once
 * if the owner signs this device (or the whole account) out.
 * Returns a stop function.
 */
export function startDeviceSession(user, onRevoked) {
  let stopped = false;
  let unsub = null;
  let fired = false;
  const revoked = () => { if (!fired && !stopped) { fired = true; onRevoked(); } };

  const beat = async (force = false) => {
    const s = ensureSession(user.uid);
    const version = currentVersion();
    // A fresh build reports in right away, not at the next 30-min heartbeat.
    if (!force && s.version === version && Date.now() - (s.lastBeat || 0) < HEARTBEAT_MS) return true;
    if (!inflight) {
      inflight = sessionsApi(user, { action: 'register', sessionId: s.id, deviceId: getDeviceId(), device: deviceInfo() })
        .finally(() => { inflight = null; });
    }
    try {
      const r = await inflight;
      if (r.revoked) { revoked(); return false; }
      const cur = readStore();
      if (cur && cur.id === s.id) writeStore({ ...cur, lastBeat: Date.now(), version });
      return true;
    } catch (err) {
      if (err.revoked) revoked();
      return false; // offline / API down: try again on the next check
    }
  };

  const listen = () => {
    if (unsub || stopped) return;
    const { id } = ensureSession(user.uid);
    unsub = onSnapshot(doc(db, 'sessions', id), (snap) => {
      if (snap.exists()) { if (snap.data().revoked) revoked(); return; }
      // Gone on the server (purged or deleted by hand): register again. A
      // device whose tokens were revoked is refused there and signs out.
      if (!snap.metadata.fromCache) beat(true);
    }, () => { /* rules not deployed yet / offline — the heartbeat still enforces */ });
  };

  // Register first, THEN listen: a listener attached before the doc exists
  // would see "missing" and re-register in a loop.
  beat().then((ok) => { if (ok) listen(); });

  const check = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    beat().then((ok) => { if (ok) listen(); });
  };
  const timer = setInterval(check, CHECK_EVERY_MS);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', check);

  return () => {
    stopped = true;
    clearInterval(timer);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', check);
    if (unsub) unsub();
  };
}

/** Normal logout: remove this device's doc (best effort, never blocks long). */
export async function endDeviceSession(user) {
  const s = readStore();
  clearSession();
  if (!user || !s || s.uid !== user.uid) return;
  try { await sessionsApi(user, { action: 'end', sessionId: s.id }, { timeoutMs: 2500 }); } catch {}
}
